import { useEffect, useMemo, useRef, useState, KeyboardEvent } from "react";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import {
  Heart,
  Ban,
  Sparkles,
  Check,
  Plus,
  X,
  Loader2,
  PartyPopper,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import type { GiftPreferences } from "@/components/card/GiftGuidanceForm";

/* ------------------------------------------------------------------ */
/* Types & constants                                                  */
/* ------------------------------------------------------------------ */

interface Props {
  preferences: GiftPreferences;
  onChange: (prefs: GiftPreferences) => void;
  /** Called whenever the user pauses editing — used for auto-save. */
  onAutoSave?: (prefs: GiftPreferences) => Promise<void> | void;
  /** Optional dislikes field, stored separately by the parent. */
  dislikes?: string;
  onDislikesChange?: (value: string) => void;
}

type SectionKey = "wants" | "has" | "dislikes" | "style";

const QUICK_PICKS: Record<SectionKey, string[]> = {
  wants: [
    "Tech gadgets",
    "Books",
    "Coffee",
    "Skincare",
    "Plants",
    "Cooking gear",
    "Fitness",
    "Art supplies",
    "Travel",
    "Music",
    "Games",
    "Jewellery",
  ],
  has: [
    "Kindle",
    "AirPods",
    "Coffee maker",
    "Yoga mat",
    "Smart watch",
    "Air fryer",
    "Bluetooth speaker",
  ],
  dislikes: [
    "Scented candles",
    "Chocolate",
    "Wool",
    "Strong perfumes",
    "Loud colours",
    "Single-use plastic",
  ],
  style: [
    "Minimalist",
    "Cozy",
    "Bold",
    "Earthy",
    "Vintage",
    "Modern",
    "Playful",
    "Eco-friendly",
    "Luxurious",
    "Sporty",
  ],
};

const LEVELS = [
  { min: 0, label: "Newcomer", emoji: "🌱" },
  { min: 25, label: "Explorer", emoji: "🔍" },
  { min: 50, label: "Insider", emoji: "🎯" },
  { min: 75, label: "Gift Pro", emoji: "🎁" },
  { min: 100, label: "Master Gifter", emoji: "👑" },
];

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */

function toList(str: string): string[] {
  return str
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function fromList(items: string[]): string {
  return items.join(", ");
}

function levelFor(progress: number) {
  return [...LEVELS].reverse().find((l) => progress >= l.min) ?? LEVELS[0];
}

/* ------------------------------------------------------------------ */
/* Reusable bits                                                      */
/* ------------------------------------------------------------------ */

function ChipList({
  items,
  onRemove,
}: {
  items: string[];
  onRemove: (idx: number) => void;
}) {
  if (items.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2">
      <AnimatePresence initial={false}>
        {items.map((item, idx) => (
          <motion.button
            key={`${item}-${idx}`}
            type="button"
            layout
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.6, opacity: 0 }}
            transition={{ type: "spring", stiffness: 400, damping: 25 }}
            onClick={() => onRemove(idx)}
            className="group inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary px-3 py-1.5 text-sm font-medium hover:bg-destructive/10 hover:text-destructive transition-colors"
          >
            <span>{item}</span>
            <X size={12} className="opacity-60 group-hover:opacity-100" />
          </motion.button>
        ))}
      </AnimatePresence>
    </div>
  );
}

function QuickPickRow({
  options,
  selected,
  onToggle,
}: {
  options: string[];
  selected: string[];
  onToggle: (value: string) => void;
}) {
  const lower = selected.map((s) => s.toLowerCase());
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const isOn = lower.includes(opt.toLowerCase());
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onToggle(opt)}
            className={cn(
              "inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-sm font-medium transition-all active:scale-95",
              isOn
                ? "border-primary bg-primary text-primary-foreground shadow-sm"
                : "border-border bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground",
            )}
          >
            {isOn ? <Check size={12} /> : <Plus size={12} />}
            <span>{opt}</span>
          </button>
        );
      })}
    </div>
  );
}

function FreeTextAdder({
  onAdd,
  placeholder,
}: {
  onAdd: (value: string) => void;
  placeholder: string;
}) {
  const [draft, setDraft] = useState("");
  const ref = useRef<HTMLInputElement>(null);

  const submit = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    onAdd(trimmed);
    setDraft("");
    ref.current?.focus();
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div className="flex gap-2">
      <Input
        ref={ref}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKey}
        onBlur={() => draft.trim() && submit()}
        placeholder={placeholder}
        className="flex-1 border-border text-sm"
      />
      <button
        type="button"
        onClick={submit}
        disabled={!draft.trim()}
        className="rounded-full bg-primary text-primary-foreground px-4 text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-primary/90 transition-colors"
        aria-label="Add"
      >
        <Plus size={16} />
      </button>
    </div>
  );
}

function SectionCard({
  icon: Icon,
  emoji,
  title,
  hint,
  filled,
  children,
}: {
  icon: React.ElementType;
  emoji: string;
  title: string;
  hint: string;
  filled: boolean;
  children: React.ReactNode;
}) {
  return (
    <motion.section
      layout
      className={cn(
        "rounded-2xl border bg-card p-5 transition-shadow",
        filled
          ? "border-primary/40 shadow-sm"
          : "border-border hover:border-border/80",
      )}
    >
      <div className="flex items-start gap-3 mb-3">
        <div
          className={cn(
            "shrink-0 w-10 h-10 rounded-xl flex items-center justify-center text-lg transition-colors",
            filled
              ? "bg-primary/15 text-primary"
              : "bg-muted text-muted-foreground",
          )}
        >
          <span aria-hidden>{emoji}</span>
          <Icon size={0} className="hidden" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-foreground">{title}</h3>
            <AnimatePresence>
              {filled && (
                <motion.span
                  initial={{ scale: 0, rotate: -90 }}
                  animate={{ scale: 1, rotate: 0 }}
                  exit={{ scale: 0 }}
                  transition={{ type: "spring", stiffness: 500, damping: 20 }}
                  className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary text-primary-foreground"
                  aria-label="Section complete"
                >
                  <Check size={12} strokeWidth={3} />
                </motion.span>
              )}
            </AnimatePresence>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>
        </div>
      </div>
      <div className="space-y-3">{children}</div>
    </motion.section>
  );
}

/* ------------------------------------------------------------------ */
/* Main                                                               */
/* ------------------------------------------------------------------ */

export default function GamifiedPreferencesEditor({
  preferences,
  onChange,
  onAutoSave,
  dislikes = "",
  onDislikesChange,
}: Props) {
  const wantsList = useMemo(() => toList(preferences.wants), [preferences.wants]);
  const hasList = useMemo(() => toList(preferences.has), [preferences.has]);
  const styleList = useMemo(() => toList(preferences.style), [preferences.style]);
  const dislikesList = useMemo(() => toList(dislikes), [dislikes]);

  /* ----- Progress -------------------------------------------------- */
  const completion = useMemo(() => {
    let filled = 0;
    if (wantsList.length > 0) filled += 1;
    if (hasList.length > 0) filled += 1;
    if (styleList.length > 0) filled += 1;
    if (dislikesList.length > 0) filled += 1;
    return Math.round((filled / 4) * 100);
  }, [
    wantsList.length,
    hasList.length,
    styleList.length,
    dislikesList.length,
  ]);

  const level = levelFor(completion);

  /* ----- One-time confetti when user crosses 100 ------------------- */
  const celebrated = useRef(false);
  useEffect(() => {
    if (completion >= 100 && !celebrated.current) {
      celebrated.current = true;
      confetti({
        particleCount: 90,
        spread: 80,
        origin: { y: 0.3 },
        colors: ["#a855f7", "#c084fc", "#e9d5ff", "#facc15", "#f472b6"],
      });
    }
    if (completion < 100) celebrated.current = false;
  }, [completion]);

  /* ----- Auto-save (debounced) ------------------------------------- */
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved">("idle");
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    if (!onAutoSave) return;
    setSaveState("saving");
    const t = setTimeout(async () => {
      try {
        await onAutoSave(preferences);
        setSaveState("saved");
        const hide = setTimeout(() => setSaveState("idle"), 1600);
        return () => clearTimeout(hide);
      } catch {
        setSaveState("idle");
      }
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferences, dislikes]);

  /* ----- Mutators -------------------------------------------------- */
  const update = (partial: Partial<GiftPreferences>) =>
    onChange({ ...preferences, ...partial });

  const toggleWants = (value: string) => {
    const exists = wantsList.some((i) => i.toLowerCase() === value.toLowerCase());
    const next = exists
      ? wantsList.filter((i) => i.toLowerCase() !== value.toLowerCase())
      : [...wantsList, value];
    update({ wants: fromList(next) });
  };
  const toggleHas = (value: string) => {
    const exists = hasList.some((i) => i.toLowerCase() === value.toLowerCase());
    const next = exists
      ? hasList.filter((i) => i.toLowerCase() !== value.toLowerCase())
      : [...hasList, value];
    update({ has: fromList(next) });
  };
  const toggleStyle = (value: string) => {
    const exists = styleList.some((i) => i.toLowerCase() === value.toLowerCase());
    const next = exists
      ? styleList.filter((i) => i.toLowerCase() !== value.toLowerCase())
      : [...styleList, value];
    update({ style: fromList(next) });
  };
  const toggleDislike = (value: string) => {
    if (!onDislikesChange) return;
    const exists = dislikesList.some(
      (i) => i.toLowerCase() === value.toLowerCase(),
    );
    const next = exists
      ? dislikesList.filter((i) => i.toLowerCase() !== value.toLowerCase())
      : [...dislikesList, value];
    onDislikesChange(fromList(next));
  };

  /* ----- Render ---------------------------------------------------- */
  return (
    <div className="space-y-6">
      {/* Hero / progress */}
      <div className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-primary/80 font-semibold">
              Your gift profile
            </p>
            <h2 className="text-xl font-bold text-foreground font-serif mt-0.5">
              {completion >= 100
                ? "All set! Friends can spoil you 🎉"
                : completion === 0
                  ? "Let's set you up for great gifts"
                  : "Looking great — keep going!"}
            </h2>
          </div>
          <motion.div
            key={level.label}
            initial={{ scale: 0.6, rotate: -10, opacity: 0 }}
            animate={{ scale: 1, rotate: 0, opacity: 1 }}
            transition={{ type: "spring", stiffness: 400, damping: 18 }}
            className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-background border border-primary/40 px-3 py-1.5 text-sm font-semibold text-primary shadow-sm"
          >
            <span aria-hidden className="text-base leading-none">
              {level.emoji}
            </span>
            <span>{level.label}</span>
          </motion.div>
        </div>

        <div className="space-y-1.5">
          <Progress value={completion} className="h-3" animate />
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">
              {completion}% complete
            </span>
            <span className="text-muted-foreground inline-flex items-center gap-1">
              {saveState === "saving" && (
                <>
                  <Loader2 size={11} className="animate-spin" /> Saving…
                </>
              )}
              {saveState === "saved" && (
                <>
                  <Check size={11} className="text-primary" /> Saved
                </>
              )}
            </span>
          </div>
        </div>

        {completion >= 100 && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-3 flex items-center gap-2 text-sm text-primary"
          >
            <PartyPopper size={14} />
            <span>You're a Master Gifter — your friends now have a cheat sheet.</span>
          </motion.div>
        )}
      </div>

      {/* Sections */}
      <SectionCard
        icon={Heart}
        emoji="💝"
        title="What you'd love"
        hint="Tap a chip or type your own. Things you want, need, or have been eyeing."
        filled={wantsList.length > 0}
      >
        <QuickPickRow
          options={QUICK_PICKS.wants}
          selected={wantsList}
          onToggle={toggleWants}
        />
        <FreeTextAdder
          onAdd={(v) => toggleWants(v)}
          placeholder="Add your own — e.g. AirPods Max"
        />
        <ChipList
          items={wantsList}
          onRemove={(idx) =>
            update({ wants: fromList(wantsList.filter((_, i) => i !== idx)) })
          }
        />
      </SectionCard>

      <SectionCard
        icon={Check}
        emoji="✋"
        title="Already got it"
        hint="So friends don't double up on something you already have."
        filled={hasList.length > 0}
      >
        <QuickPickRow
          options={QUICK_PICKS.has}
          selected={hasList}
          onToggle={toggleHas}
        />
        <FreeTextAdder
          onAdd={(v) => toggleHas(v)}
          placeholder="Add an item — e.g. Stanley cup"
        />
        <ChipList
          items={hasList}
          onRemove={(idx) =>
            update({ has: fromList(hasList.filter((_, i) => i !== idx)) })
          }
        />
      </SectionCard>

      {onDislikesChange && (
        <SectionCard
          icon={Ban}
          emoji="🚫"
          title="Please, no thanks"
          hint="Things you'd rather not receive — allergies, dislikes, you name it."
          filled={dislikesList.length > 0}
        >
          <QuickPickRow
            options={QUICK_PICKS.dislikes}
            selected={dislikesList}
            onToggle={toggleDislike}
          />
          <FreeTextAdder
            onAdd={(v) => toggleDislike(v)}
            placeholder="Add your own — e.g. anything floral"
          />
          <ChipList
            items={dislikesList}
            onRemove={(idx) =>
              onDislikesChange(
                fromList(dislikesList.filter((_, i) => i !== idx)),
              )
            }
          />
        </SectionCard>
      )}

      <SectionCard
        icon={Sparkles}
        emoji="🎨"
        title="Your vibe"
        hint="Colours, aesthetics, themes — give friends a feel for your style."
        filled={styleList.length > 0}
      >
        <QuickPickRow
          options={QUICK_PICKS.style}
          selected={styleList}
          onToggle={toggleStyle}
        />
        <FreeTextAdder
          onAdd={(v) => toggleStyle(v)}
          placeholder="Add a vibe — e.g. warm neutrals"
        />
        <ChipList
          items={styleList}
          onRemove={(idx) =>
            update({ style: fromList(styleList.filter((_, i) => i !== idx)) })
          }
        />
      </SectionCard>
    </div>
  );
}
